import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../utils/api';
import { Trophy, ChevronRight, ChevronLeft, CheckCircle } from 'lucide-react';

export default function QuestionnairePage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get('/auth/questionnaire/questions')
      .then(r => setQuestions(r.data.questions || []))
      .catch(() => toast.error('Failed to load questionnaire'))
      .finally(() => setLoading(false));
  }, []);

  const currentQ = questions[step];
  const totalSteps = questions.length;
  const allAnswered = questions.every(q => answers[q.id] !== undefined);
  const progress = Math.round((Object.keys(answers).length / Math.max(totalSteps, 1)) * 100);

  const selectAnswer = (qid, value) => {
    setAnswers(a => ({ ...a, [qid]: value }));
    // Auto-advance after short delay
    if (step < totalSteps - 1) {
      setTimeout(() => setStep(s => s + 1), 300);
    }
  };

  const handleSubmit = async () => {
    const payload = questions.map(q => ({ question_id: q.id, answer: answers[q.id] ?? 0 }));
    setSubmitting(true);
    try {
      const res = await api.post('/auth/questionnaire', { answers: payload });
      setResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinue = () => {
    // Refresh user state via /me
    api.get('/auth/me').then(r => {
      // Force page reload to pick up new user data
      window.location.href = '/clubs';
    }).catch(() => {
      navigate('/clubs');
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-400 text-sm">Loading questionnaire...</div>
      </div>
    );
  }

  // Show result screen
  if (result) {
    const ratingLevel = result.initial_rating >= 1700 ? 'Expert' :
      result.initial_rating >= 1600 ? 'Advanced' :
      result.initial_rating >= 1500 ? 'Upper Intermediate' :
      result.initial_rating >= 1400 ? 'Intermediate' : 'Beginner';
    const ratingColor = result.initial_rating >= 1700 ? 'text-red-600' :
      result.initial_rating >= 1600 ? 'text-amber-600' :
      result.initial_rating >= 1500 ? 'text-green-600' :
      result.initial_rating >= 1400 ? 'text-blue-600' : 'text-slate-600';

    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="questionnaire-result">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-2xl mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Your Initial Rating
          </h1>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 mb-4">
            <p className={`text-5xl font-bold font-mono ${ratingColor}`} data-testid="initial-rating">
              {result.initial_rating}
            </p>
            <p className={`text-sm font-semibold mt-1 ${ratingColor}`}>{ratingLevel}</p>
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-left">
              <p className="text-xs font-semibold text-amber-800 mb-1">Provisional Rating</p>
              <p className="text-xs text-amber-700">
                This rating will adjust quickly as you play. After <strong>25 rated games</strong> against <strong>15 unique opponents</strong>, your rating becomes verified.
              </p>
            </div>
          </div>
          <button onClick={handleContinue}
            className="w-full h-11 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-all active:scale-[0.98]"
            data-testid="continue-to-app-btn">
            Continue to App
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="questionnaire-page">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-green-600 rounded-2xl mb-3 shadow-lg">
            <Trophy className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Skill Assessment
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Help us determine your starting rating</p>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
            <span>Question {step + 1} of {totalSteps}</span>
            <span>{progress}% complete</span>
          </div>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-green-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Question card */}
        {currentQ && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-4" data-testid={`question-${currentQ.id}`}>
            <p className="text-base font-semibold text-slate-900 mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {currentQ.question}
            </p>
            <div className="space-y-2.5">
              {currentQ.options.map(opt => {
                const selected = answers[currentQ.id] === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => selectAnswer(currentQ.id, opt.value)}
                    className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all text-sm ${
                      selected
                        ? 'border-green-500 bg-green-50 text-green-800 font-medium'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`option-${currentQ.id}-${opt.value}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        selected ? 'border-green-500 bg-green-500' : 'border-slate-300'
                      }`}>
                        {selected && <div className="w-2 h-2 rounded-full bg-white" />}
                      </div>
                      {opt.label}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setStep(s => Math.max(0, s - 1))}
            disabled={step === 0}
            className="flex items-center gap-1 h-10 px-4 text-sm font-medium text-slate-500 hover:text-slate-700 disabled:opacity-30 transition-all"
            data-testid="prev-question-btn"
          >
            <ChevronLeft size={16} /> Previous
          </button>

          {step < totalSteps - 1 ? (
            <button
              onClick={() => setStep(s => Math.min(totalSteps - 1, s + 1))}
              className="flex items-center gap-1 h-10 px-4 text-sm font-medium text-green-600 hover:text-green-700 transition-all"
              data-testid="next-question-btn"
            >
              Next <ChevronRight size={16} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!allAnswered || submitting}
              className="h-10 px-6 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white text-sm font-semibold rounded-lg transition-all active:scale-[0.98]"
              data-testid="submit-questionnaire-btn"
            >
              {submitting ? 'Calculating...' : 'Get My Rating'}
            </button>
          )}
        </div>

        {/* Quick nav dots */}
        <div className="flex justify-center gap-1.5 mt-6">
          {questions.map((q, i) => (
            <button
              key={q.id}
              onClick={() => setStep(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                i === step ? 'bg-green-500 scale-110' :
                answers[q.id] !== undefined ? 'bg-green-300' : 'bg-slate-200'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
